import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useProjectContext } from '~/hooks';
import { pollAsyncRequestResult } from '~/api/projects';

const SESSION_KEY_PREFIX = 'documentIngestionApiEventId_';
const POLL_INTERVAL_MS = 5000;
const PROCESSING_MESSAGE = 'Extracting and analyzing document…';

export type DocumentIngestionStatus =
  | 'idle'
  | 'uploading'
  | 'processing'
  | 'error';

interface State {
  status: DocumentIngestionStatus;
  apiEventId: string | null;
  statusMessage: string | null;
  errorMessage: string | null;
}

interface ContextValue extends State {
  startTracking: (apiEventId: string) => void;
  setUploading: (message?: string) => void;
  setError: (message: string) => void;
  dismissError: () => void;
}

const initialState: State = {
  status: 'idle',
  apiEventId: null,
  statusMessage: null,
  errorMessage: null,
};

const DocumentIngestionProgressContext = createContext<ContextValue | null>(null);

function readStoredApiEventId(projectId: string): string | null {
  try {
    return sessionStorage.getItem(SESSION_KEY_PREFIX + projectId);
  } catch {
    return null;
  }
}

function writeStoredApiEventId(projectId: string, apiEventId: string): void {
  try {
    sessionStorage.setItem(SESSION_KEY_PREFIX + projectId, apiEventId);
  } catch {
    // ignore
  }
}

function clearStoredApiEventId(projectId: string): void {
  try {
    sessionStorage.removeItem(SESSION_KEY_PREFIX + projectId);
  } catch {
    // ignore
  }
}

export function DocumentIngestionProgressProvider({ children }: { children: ReactNode }) {
  const { project, fetchProject } = useProjectContext();
  const projectId = project?.id ?? '';
  const pendingEventFromProject = (project as any)?.apiEvents?.document_ingestion ?? null;
  const pendingEventId: string | null =
    pendingEventFromProject?.status === 'PENDING' ? pendingEventFromProject.id : null;

  const queryClient = useQueryClient();
  const [state, setState] = useState<State>(initialState);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Reset on project change.
  useEffect(() => {
    setState(initialState);
  }, [projectId]);

  // Resume polling on mount / project change / when the server reports a new
  // PENDING document_ingestion event.
  useEffect(() => {
    if (!projectId) return;
    const stored = readStoredApiEventId(projectId);
    const resumeId = stored ?? pendingEventId;
    if (!resumeId) return;
    setState((prev) => {
      // Don't clobber an in-flight upload or an error the user hasn't dismissed.
      if (prev.status === 'uploading' || prev.status === 'error') return prev;
      if (prev.apiEventId === resumeId && prev.status === 'processing') return prev;
      return {
        status: 'processing',
        apiEventId: resumeId,
        statusMessage: PROCESSING_MESSAGE,
        errorMessage: null,
      };
    });
  }, [projectId, pendingEventId]);

  // Polling effect: while processing a known apiEventId, poll until terminal.
  useEffect(() => {
    if (state.status !== 'processing' || !state.apiEventId || !projectId) return;

    const apiEventId = state.apiEventId;

    const clearPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleSuccess = () => {
      clearPolling();
      clearStoredApiEventId(projectId);
      queryClient.invalidateQueries({ queryKey: ['ingestionHistory', projectId] });
      // Refresh the project so the worksheet / summary reflect the imported data.
      Promise.resolve(fetchProject?.()).catch(() => { });
      toast.success('Document imported successfully.');
      setState(initialState);
    };

    const handleError = (message: string) => {
      clearPolling();
      clearStoredApiEventId(projectId);
      queryClient.invalidateQueries({ queryKey: ['ingestionHistory', projectId] });
      Promise.resolve(fetchProject?.()).catch(() => { });
      toast.error(message);
      setState({
        status: 'error',
        apiEventId: null,
        statusMessage: null,
        errorMessage: message,
      });
    };

    const poll = async () => {
      try {
        const event = await pollAsyncRequestResult(projectId, apiEventId);
        if (event.status === 'SUCCESS') {
          handleSuccess();
        } else if (event.status === 'ERROR') {
          const errResult = event.result as { message?: string } | undefined;
          handleError(errResult?.message ?? 'Document import failed.');
        }
      } catch (err: any) {
        const message =
          err?.response?.data?.errorMessage ??
          err?.response?.data?.message ??
          'Document import failed.';
        handleError(message);
      }
    };

    intervalRef.current = setInterval(poll, POLL_INTERVAL_MS);
    poll();

    return clearPolling;
  }, [state.status, state.apiEventId, projectId, queryClient, fetchProject]);

  const startTracking = useCallback(
    (apiEventId: string) => {
      if (!projectId) return;
      writeStoredApiEventId(projectId, apiEventId);
      setState({
        status: 'processing',
        apiEventId,
        statusMessage: PROCESSING_MESSAGE,
        errorMessage: null,
      });
    },
    [projectId]
  );

  const setUploading = useCallback((message?: string) => {
    setState({
      status: 'uploading',
      apiEventId: null,
      statusMessage: message ?? 'Uploading document…',
      errorMessage: null,
    });
  }, []);

  const setError = useCallback((message: string) => {
    setState({
      status: 'error',
      apiEventId: null,
      statusMessage: null,
      errorMessage: message,
    });
  }, []);

  const dismissError = useCallback(() => {
    setState(initialState);
  }, []);

  const value: ContextValue = {
    ...state,
    startTracking,
    setUploading,
    setError,
    dismissError,
  };

  return (
    <DocumentIngestionProgressContext.Provider value={value}>
      {children}
    </DocumentIngestionProgressContext.Provider>
  );
}

export function useDocumentIngestionProgress(): ContextValue | null {
  return useContext(DocumentIngestionProgressContext);
}
